import { ExtractPropTypes, onMounted, ref, watch, SetupContext } from 'vue'
import { useRoute } from 'vue-router'
import { logLlmEventToRum } from '@/libs/features/llm/logging'
import {
  KnowledgeBaseLlmResponse,
  KnowledgeBaseSourceDocument,
  LlmDatadogLogEvent,
  LlmDatadogLoggingDetails,
  LlmFeature,
} from '@/libs/features/llm/types'
import { useWhoAmI } from '@/store/profile/whoAmI'
import { buildLlmDatadogLoggingDetails } from '@/utils/llm/loggingHelpers'
import { AskSopInputForm } from '../AskSOPModalInputs/types'
import { ASK_SOP_RESPONSE_PROPS } from './types'

const EXTRA_FEEDBACK_REASONS = ['Outdated SOP']
export type AskSopResponsePropType = ExtractPropTypes<
  typeof ASK_SOP_RESPONSE_PROPS
>

/**
 *
 * @param props
 * @param context
 */
export default function (props: AskSopResponsePropType, context: SetupContext) {
  const responseText = ref('')
  const sources = ref<KnowledgeBaseSourceDocument[]>([])
  const { selfEntity, roles } = useWhoAmI()
  const route = useRoute()

  const loggingDetails = ref<LlmDatadogLoggingDetails | undefined>(undefined)

  const showLink = (source: KnowledgeBaseSourceDocument) =>
    !source.documentName.startsWith('Playbook')
  /**
   *
   */
  function reset() {
    responseText.value = ''
    sources.value = []
    loggingDetails.value = undefined
    context.emit('reset')
  }

  /**
   *
   * @param inputs
   * @param response
   */
  function calculateLoggingDetails(
    inputs: AskSopInputForm | undefined,
    response: KnowledgeBaseLlmResponse
  ) {
    return {
      ...buildLlmDatadogLoggingDetails({
        response,
        route,
        feature: LlmFeature.ASK_THYME_BOX,
        query: props.inputs?.query,
        devToolOptions: props.inputs?.devToolOptions,
        person: selfEntity.value?.person,
        roles: roles.value,
      }),
      response: response.response,
      sources: response.sourceDocuments.map(
        (source: KnowledgeBaseSourceDocument) => source.documentName
      ),
      references: sources.value.map((source) => source.documentName),
      userContext: inputs?.userContext,
    } as LlmDatadogLoggingDetails
  }

  /**
   * Get double newlines to render by adding a non-breaking space in between lines
   * @param text
   */
  function fixMarkdown(text: string) {
    return text.replaceAll('\n\n', '\n\n&nbsp;\n\n')
  }

  /**
   *
   * @param sources
   */
  function deduplicateSources(
    sources: KnowledgeBaseSourceDocument[]
  ): KnowledgeBaseSourceDocument[] {
    const uniqueSources = new Map<string, KnowledgeBaseSourceDocument>()
    for (const source of sources) {
      uniqueSources.set(source.documentId, source)
    }
    return [...uniqueSources.values()]
  }

  /**
   *
   * @param response
   */
  function handleIncomingResponse(response: KnowledgeBaseLlmResponse) {
    responseText.value = fixMarkdown(response.response)
    sources.value = deduplicateSources(response.sourceDocuments)
    loggingDetails.value = calculateLoggingDetails(props.inputs, response)
    logLlmEventToRum(LlmDatadogLogEvent.QUERY, loggingDetails.value)
  }

  watch(
    () => props.response,
    () => {
      if (props.response) {
        handleIncomingResponse(props.response)
      }
    }
  )
  onMounted(() => {
    if (props.response) {
      handleIncomingResponse(props.response)
    }
  })

  return {
    responseText,
    sources,
    loggingDetails,
    EXTRA_FEEDBACK_REASONS,
    reset,
    showLink,
  }
}
